import { useWithdrawCryptoMutation } from '@/features/crypto/cryptoApi';
import styles from '@/shared/components/DepositWithdraw/DepositWithdraw.module.css';
import GenericFormInput from '@/shared/components/GenericFormInput/GenericFormInput';
import { maxWithdrawalAmountLength } from '@/shared/constants/constants';
import { validateCrypto } from '@/shared/helpers/crypto';
import { PERCENTAGES } from '@/shared/utils/constants';
import { AssetBalance } from '@/types/balances';
import { normalizedBig } from '@cex/shared-modules';
import { IonButton } from '@ionic/react';
import Big from 'big.js';
import { t } from 'i18next';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PercentageButton from '../../PercentageButton/PercentageButton';
import TermsConditionsWithdrawals from '../../TermsConditionsWithdrawals/TermsConditionsWithdrawals';
import { WhitelistedAddress } from '@/types/whitelist';
import { userCanWithdraw, useUser } from '@/shared/hooks/user';
import AddressSelector from '@/shared/components/DepositWithdraw/CryptoWithdrawal/AddressSelector';
import { Address } from '@/types';

type Props = {
  assetId: string;
  availableAssetQuantity: Big;
  dismissModal: () => void;
  isOpen: boolean;
  modal: RefObject<HTMLIonModalElement>;
  assetBalance: AssetBalance;
  whitelistedAddresses?: WhitelistedAddress[];
  setIsOpen: (value: boolean) => void;
  handleInitTravelRule?: (value: boolean, withdrawCryptoData: any) => void;
  targetAddress?: Address | null;
  setTargetAddress?: (address: Address) => void;
};

const CryptoWithdrawal: React.FC<Props> = ({
  assetId,
  availableAssetQuantity,
  assetBalance,
  whitelistedAddresses,
  setIsOpen,
  handleInitTravelRule,
  targetAddress,
  setTargetAddress
}) => {
  const [quantityCrypto, setQuantityCrypto] = useState<Big | null>(null);
  const [displayValue, setDisplayValue] = useState<string | null>(null);
  const [isValidTargetAddress, setIsValidTargetAddress] = useState<boolean>();
  const [amountError, setAmountError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState({
    termsIncorrectAddress: false
  });
  const [showValidityInfo, setShowValiditiyInfo] = useState(false);

  const [withdrawCrypto, { data: withdrawCryptoData, error, isSuccess: withdrawalInitSuccessful }] =
    useWithdrawCryptoMutation();

  const hasCalledInitTravelRule = useRef(false);

  const initTravelRule = withdrawalInitSuccessful && withdrawCryptoData;

  const user = useUser();

  const userHasWithdrawalPermission = user && userCanWithdraw(user);

  const handleMaxAction = () => {
    validateQuantity(availableAssetQuantity.toString());
    setQuantityCrypto(availableAssetQuantity);
    setDisplayValue(availableAssetQuantity.toString());
  };

  const handleSetPercentageValue = (percentage: number) => {
    const percentageAmount = (Number(availableAssetQuantity) * (percentage / 100)).toString();
    validateQuantity(percentageAmount);
    setDisplayValue(percentageAmount);
  };

  const withdrawCryptoHandler = async () => {
    setShowValiditiyInfo(true);
    if (!amountError && quantityCrypto && !isWithdrawalDisabled && allTermsAccepted && targetAddress) {
      await withdrawCrypto({
        amount: quantityCrypto,
        asset_id: assetId,
        target_address: targetAddress.value,
        destination_tag: targetAddress?.tag
      });
      setIsOpen(true);
    }
  };

  const handleTargetAddressChange = (address: Address, isValid: boolean) => {
    setTargetAddress && setTargetAddress(address);
    setIsValidTargetAddress(isValid);
  };

  const validateQuantity = (value: string) => {
    setShowValiditiyInfo(false);
    const bigQuantityCrypto = normalizedBig(value);
    setQuantityCrypto(bigQuantityCrypto);
    setDisplayValue(value);
    const error = validateCrypto(value, assetId, availableAssetQuantity);
    setAmountError(error);
  };

  const handleTerms = useCallback(
    (type) => {
      setShowValiditiyInfo(false);
      setAcceptedTerms({ ...acceptedTerms, [type]: !acceptedTerms[type] });
    },
    [acceptedTerms]
  );

  const allTermsAccepted = useMemo(() => Object.values(acceptedTerms).every((value) => value), [acceptedTerms]);

  const isWithdrawalDisabled = useMemo(() => {
    return !quantityCrypto || amountError !== null || !targetAddress || !userHasWithdrawalPermission;
  }, [quantityCrypto, targetAddress, amountError, userHasWithdrawalPermission]);

  useEffect(() => {
    if (initTravelRule && handleInitTravelRule && !hasCalledInitTravelRule.current) {
      handleInitTravelRule(true, withdrawCryptoData);
      hasCalledInitTravelRule.current = true;
    }
  }, [initTravelRule, handleInitTravelRule, withdrawCryptoData]);

  return (
    <>
      <div className={styles.sectionSpacing}>
        <GenericFormInput
          assetId={assetId}
          displayValue={displayValue}
          errorMessage={amountError}
          fixedLabel={assetId?.toUpperCase()}
          id={'withdrawalQuantityCrypto'}
          inputmode="decimal"
          isValidUserInput={amountError === null}
          label={t('general.amount')}
          max={maxWithdrawalAmountLength}
          name={'withdrawalQuantityCrypto'}
          setValue={validateQuantity}
          type={'amount'}
          showValidityInfo={amountError !== null}
          {...(assetBalance && handleMaxAction)}
        />
      </div>

      <div className={styles.percentagesContainer}>
        {PERCENTAGES.map((percentage) => (
          <span className={styles.percentageItem} key={percentage}>
            <PercentageButton percentage={percentage} percentageHandler={handleSetPercentageValue} />
          </span>
        ))}
      </div>

      <AddressSelector
        assetId={assetId}
        address={targetAddress!}
        onAddressChanged={handleTargetAddressChange}
        hasTag={assetId === 'xrp'}
        whitelistedAddresses={whitelistedAddresses}
      />

      <TermsConditionsWithdrawals termsHandler={handleTerms} termsError={!allTermsAccepted && showValidityInfo} />

      <div className={styles.buttonContainer}>
        <IonButton
          id="crypto-modal"
          expand="block"
          type="submit"
          shape="round"
          disabled={isWithdrawalDisabled || !allTermsAccepted || !isValidTargetAddress}
          onClick={withdrawCryptoHandler}
          className={styles.withdrawButton}
        >
          <p className={styles.buttonText}>{t('wallet.withdraw')}</p>
        </IonButton>
      </div>

      {/*{assetBalance && withdrawCryptoData && targetAddress && (*/}
      {/*  <CryptoWithdrawalModal*/}
      {/*    dismissModal={dismissModal}*/}
      {/*    modal={modal}*/}
      {/*    withdrawalData={withdrawCryptoData}*/}
      {/*    isOpen={isOpen}*/}
      {/*    targetAddress={targetAddress}*/}
      {/*  />*/}
      {/*)}*/}
    </>
  );
};

export default CryptoWithdrawal;
